import { useEffect, useMemo, useRef, useState } from 'react';
import { Application, Assets, Container, Graphics, Sprite, Text } from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { TILE_H, TILE_W } from '../../shopfloor/config/constants';
import { calculateDepthSort, isoToScreen } from '../../shopfloor/config/iso';
import { buildIsoLineLayout } from '../../line/lineLayout';
import { lineAssetProfile, lineAssetUrls, nodeAssetProfile, robotAssetProfile } from '../../line/lineAssets';
import { stationPartCount, stationVisual } from '../../line/lineVisuals';

function drawDiamond(graphics, widthTiles, heightTiles, fill, stroke, alpha = 1, strokeWidth = 2) {
  const rightX = widthTiles * TILE_W / 2;
  const rightY = widthTiles * TILE_H / 2;
  const bottomX = (widthTiles - heightTiles) * TILE_W / 2;
  const bottomY = (widthTiles + heightTiles) * TILE_H / 2;
  const leftX = -heightTiles * TILE_W / 2;
  const leftY = heightTiles * TILE_H / 2;
  graphics.poly([0, 0, rightX, rightY, bottomX, bottomY, leftX, leftY]);
  graphics.fill({ color: fill, alpha });
  graphics.stroke({ color: stroke, width: strokeWidth, alpha: Math.min(1, alpha + 0.2) });
}

function makeText(text, size = 11, fill = 0xe2e8f0, weight = '600') {
  const label = new Text({
    text,
    style: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: size,
      fontWeight: weight,
      fill,
      stroke: { color: 0x0d1b2a, width: 3 },
    },
  });
  label.anchor.set(0.5, 1);
  return label;
}

export default function LineIsoView({ topology, state, selectedKey, onSelect, debugAnchors = false,
                                     showRoute = true, showRobotLinks = true }) {
  const hostRef = useRef(null);
  const appRef = useRef(null);
  const viewportRef = useRef(null);
  const fittedRef = useRef(false);
  const worldRef = useRef(null);
  const textureRef = useRef({});
  const [ready, setReady] = useState(false);
  const layout = useMemo(() => buildIsoLineLayout(topology), [topology]);

  useEffect(() => {
    if (!hostRef.current) return undefined;
    let destroyed = false;
    let app;
    let canvas;

    const init = async () => {
      app = new Application();
      await app.init({
        background: 0x0d1b2a,
        antialias: true,
        resizeTo: hostRef.current,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });
      if (destroyed || !hostRef.current) {
        try { app.destroy(true, { children: true }); } catch { /* noop */ }
        return;
      }

      canvas = app.canvas;
      hostRef.current.innerHTML = '';
      hostRef.current.appendChild(canvas);
      appRef.current = app;

      const viewport = new Viewport({
        screenWidth: app.screen.width,
        screenHeight: app.screen.height,
        worldWidth: 1800,
        worldHeight: 1000,
        events: app.renderer.events,
      });
      viewport.drag().pinch().wheel().decelerate().clampZoom({ minScale: 0.35, maxScale: 2.4 });
      viewportRef.current = viewport;
      app.stage.addChild(viewport);

      const world = new Container();
      world.sortableChildren = true;
      worldRef.current = world;
      viewport.addChild(world);

      // Load every art asset declared for this topology once: machines, conveyors and robots.
      // The renderer never needs to know where the art came from or how it was cropped.
      const spriteUrls = lineAssetUrls(topology);
      for (const url of spriteUrls) {
        textureRef.current[url] = await Assets.load(url).catch(() => null);
      }
      if (destroyed) return;
      // Both 8x8 cell diamonds together span roughly x=-384..1440 / y=96..480.
      // Center the camera on the whole line rather than on cell 1.
      // Initial guess only. `fitToContent` below replaces it once the world has been drawn and its
      // real bounds are known — a hardcoded centre/zoom cannot know how wide the line is, and at
      // 1600px it left two of the eight stations off-canvas with no indication.
      viewport.moveCenter(528, 288);
      viewport.setZoom(0.82);
      setReady(true);
    };

    init();

    return () => {
      destroyed = true;
      setReady(false);
      try {
        if (app && typeof app.destroy === 'function') app.destroy(true, { children: true });
      } catch { /* Pixi can race async teardown in React strict mode */ }
      try {
        if (canvas?.parentNode) canvas.parentNode.removeChild(canvas);
      } catch { /* noop */ }
      appRef.current = null;
      viewportRef.current = null;
      worldRef.current = null;
      textureRef.current = {};
    };
  }, [topology]);

  useEffect(() => {
    if (!ready || !worldRef.current) return;
    const world = worldRef.current;
    const removed = world.removeChildren();
    removed.forEach((child) => {
      try { child.destroy({ children: true }); } catch { /* noop */ }
    });

    // Cell floors.
    Object.entries(layout.cells).forEach(([cellId, cell]) => {
      const cellDef = topology.cells.find((c) => c.id === cellId);
      const constrained = topology.balance?.constraintCellId === cellId;
      const g = new Graphics();
      const pos = isoToScreen(cell.tx, cell.ty);
      g.x = pos.x;
      g.y = pos.y;
      drawDiamond(g, cell.width, cell.height, constrained ? 0x26394a : 0x152840, constrained ? 0xa89060 : 0x4a5568, 0.72, constrained ? 3 : 1.5);
      g.zIndex = -1000;
      world.addChild(g);

      const label = makeText(
        `${cellDef?.name || cellId} · ${cellDef?.piecesPerHour ?? '—'} pcs/h`,
        12,
        constrained ? 0xa89060 : 0x8ba0b5,
        '700',
      );
      const top = isoToScreen(cell.tx + 0.5, cell.ty + 0.5);
      label.x = top.x;
      label.y = top.y - 8;
      label.zIndex = -900;
      world.addChild(label);
    });

    // Robot-sequence traces (physical handling circuit). Off by default in the iso view: two full
    // circuits plus the route is more ink than a floor needs, and the route is the one people read.
    if (showRobotLinks) (topology.cells || []).forEach((cell) => {
      const seq = cell.robotSequence || [];
      const g = new Graphics();
      let started = false;
      for (const key of seq) {
        const p = layout.points[key];
        if (!p) continue;
        const s = isoToScreen(p.tx, p.ty);
        if (!started) {
          g.moveTo(s.x, s.y);
          started = true;
        } else {
          g.lineTo(s.x, s.y);
        }
      }
      g.stroke({ color: 0x8ba0b5, width: 2, alpha: 0.2 });
      g.zIndex = -500;
      world.addChild(g);
    });

    // The part's path. Drawn through `routePath` — the conveyors and posts included — because a part
    // does not teleport from OP20 to OP30, it rides a conveyor between them. Falling back to `route`
    // keeps an older backend working, just with the machines joined directly.
    if (showRoute) {
    const routeLine = new Graphics();
    (topology.routePath || topology.route || []).forEach((key, i) => {
      const p = layout.points[key];
      if (!p) return;
      const s = isoToScreen(p.tx, p.ty);
      if (i === 0) routeLine.moveTo(s.x, s.y);
      else routeLine.lineTo(s.x, s.y);
    });
    routeLine.stroke({ color: 0xa89060, width: 5, alpha: 0.72 });
    routeLine.zIndex = -400;
    world.addChild(routeLine);
    }

    // Non-machine handled points. Conveyors use Vicente's isometric pack; generic posts stay
    // geometric until we have honest art for them.
    (topology.nodes || []).forEach((node) => {
      const p = layout.points[node.key];
      if (!p) return;
      const s = isoToScreen(p.tx, p.ty);
      const c = new Container();
      c.x = s.x;
      c.y = s.y;
      c.zIndex = calculateDepthSort(p.tx, p.ty);
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', () => onSelect(node.key));

      const profile = nodeAssetProfile(node);
      if (profile?.spriteUrl && textureRef.current[profile.spriteUrl]) {
        const footprint = profile.footprint;
        const floorContact = {
          x: (footprint.w - footprint.h) * TILE_W / 2,
          y: (footprint.w + footprint.h) * TILE_H / 2,
        };

        const base = new Graphics();
        drawDiamond(base, footprint.w, footprint.h, 0x13253b,
                    selectedKey === node.key ? 0xe2e8f0 : 0x5f7188, 0.58,
                    selectedKey === node.key ? 3 : 1);
        c.addChild(base);

        const sprite = new Sprite(textureRef.current[profile.spriteUrl]);
        sprite.anchor.set(profile.groundAnchor.x, profile.groundAnchor.y);
        const scale = profile.renderWidthPx / Math.max(1, sprite.texture.width);
        sprite.scale.set(scale);
        sprite.x = floorContact.x + profile.groundOffsetPx.x;
        sprite.y = floorContact.y + profile.groundOffsetPx.y;
        c.addChild(sprite);

        const label = makeText('CONVEYOR', 7.5, 0x8ba0b5, '700');
        label.x = floorContact.x;
        label.y = floorContact.y - profile.labelLiftPx;
        c.addChild(label);

        if (debugAnchors) {
          const guide = new Graphics();
          guide.moveTo(floorContact.x - 10, floorContact.y);
          guide.lineTo(floorContact.x + 10, floorContact.y);
          guide.moveTo(floorContact.x, floorContact.y - 10);
          guide.lineTo(floorContact.x, floorContact.y + 10);
          guide.stroke({ color: 0x22d3ee, width: 2, alpha: 1 });
          c.addChild(guide);
        }
      } else {
        const g = new Graphics();
        drawDiamond(g, 0.9, 0.9, 0x1c3557,
                    selectedKey === node.key ? 0xe2e8f0 : 0x8ba0b5, 0.95,
                    selectedKey === node.key ? 3 : 1);
        c.addChild(g);
        const label = makeText('POST', 8, 0x8ba0b5, '700');
        label.y = -4;
        c.addChild(label);
      }
      world.addChild(c);
    });

    // Machines/stations.
    (topology.stations || []).forEach((station) => {
      const p = layout.points[station.key];
      if (!p) return;
      const s = isoToScreen(p.tx, p.ty);
      const stationState = state?.stations?.[station.key];
      const visual = stationVisual(stationState);
      const selected = selectedKey === station.key;
      const qualityTarget = (state?.quality?.stationsAwaitingApproval || []).includes(station.key);
      const c = new Container();
      c.x = s.x;
      c.y = s.y;
      c.zIndex = calculateDepthSort(p.tx, p.ty, 50);
      c.alpha = visual.present ? 1 : 0.55;
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', () => onSelect(station.key));

      const profile = lineAssetProfile(station.machineType, station.key);
      const footprint = profile.footprint || { w: 2, h: 2 };
      const floorContact = {
        x: (footprint.w - footprint.h) * TILE_W / 2,
        y: (footprint.w + footprint.h) * TILE_H / 2,
      };

      const base = new Graphics();
      const baseStroke = selected ? 0xe2e8f0 : qualityTarget ? 0xf59e0b : visual.pixi;
      drawDiamond(base, footprint.w, footprint.h, 0x152840, baseStroke, 0.94,
                  selected ? 4 : qualityTarget ? 4 : 2.5);
      c.addChild(base);

      if (qualityTarget) {
        const glow = new Graphics();
        drawDiamond(glow, footprint.w + 0.18, footprint.h + 0.18, 0x000000, 0xf59e0b, 0, 2);
        glow.alpha = 0.9;
        c.addChildAt(glow, 0);
      }

      const texture = profile.spriteUrl ? textureRef.current[profile.spriteUrl] : null;
      if (texture) {
        const sprite = new Sprite(texture);
        sprite.anchor.set(profile.groundAnchor.x, profile.groundAnchor.y);
        const scale = profile.renderWidthPx / Math.max(1, sprite.texture.width);
        sprite.scale.set(scale);
        // THIS is the invariant: art moves to the logical floor contact; logical geometry never
        // moves to compensate for transparent pixels or odd sprite crops.
        sprite.x = floorContact.x + profile.groundOffsetPx.x;
        sprite.y = floorContact.y + profile.groundOffsetPx.y;
        c.addChild(sprite);
      } else {
        // The stand-in obeys the SAME invariant as the sprite: it is placed on the logical floor
        // contact, not on the container origin. Drawing it at the origin put its base 96px above the
        // crosshair for a 2x2 footprint ((w + h) * TILE_H / 2) — so the four stations without art
        // (BALANCER, MILL, STATION, GAUGE) floated while the lathes looked fine, which reads as an
        // art-calibration problem and is not one.
        const body = new Graphics();
        body.roundRect(floorContact.x - 54, floorContact.y - 70, 108, 70, 8);
        body.fill({ color: 0x1c3557, alpha: 0.98 });
        body.stroke({ color: visual.pixi, width: 2, alpha: 0.9 });
        c.addChild(body);
        const type = makeText(station.machineType, 9, 0x8ba0b5, '700');
        type.x = floorContact.x;
        type.y = floorContact.y - 24;
        c.addChild(type);
      }

      // Calibration overlay: cyan crosshair = the exact logical floor contact used by the sprite.
      // If the machine base does not sit on this crosshair, change lineAssets.js — never tx/ty.
      if (debugAnchors) {
        const guide = new Graphics();
        guide.moveTo(floorContact.x - 14, floorContact.y);
        guide.lineTo(floorContact.x + 14, floorContact.y);
        guide.moveTo(floorContact.x, floorContact.y - 14);
        guide.lineTo(floorContact.x, floorContact.y + 14);
        guide.stroke({ color: 0x22d3ee, width: 2, alpha: 1 });
        guide.circle(floorContact.x, floorContact.y, 4);
        guide.fill({ color: 0x22d3ee, alpha: 1 });
        c.addChild(guide);

        const calibration = makeText(
          `${profile.key} · anchor ${profile.groundAnchor.x.toFixed(2)},${profile.groundAnchor.y.toFixed(2)}`,
          7,
          0x22d3ee,
          '700',
        );
        calibration.x = floorContact.x;
        calibration.y = floorContact.y + 36;
        c.addChild(calibration);
      }

      const label = makeText(station.key, 13, 0xe2e8f0, '800');
      label.y = -profile.labelLiftPx;
      c.addChild(label);

      const stateLabel = makeText(visual.label, 8, visual.pixi, '800');
      stateLabel.y = -profile.statusLiftPx;
      c.addChild(stateLabel);

      if (qualityTarget) {
        const action = makeText('OFFSET ACTION', 8, 0xf59e0b, '900');
        action.y = -profile.labelLiftPx - 18;
        c.addChild(action);
      }

      if (visual.alarm) {
        const alarm = new Graphics();
        alarm.circle(profile.alarmOffsetPx.x, profile.alarmOffsetPx.y, 8);
        alarm.fill(0xc0392b);
        c.addChild(alarm);
      }

      const wip = state?.wip?.[station.key] ?? 0;
      const parts = stationPartCount(stationState);
      if (wip > 0 || visual.present) {
        const info = makeText(`P ${parts} · WIP ${wip}`, 8, 0x8ba0b5, '600');
        info.y = TILE_H * 2 + 20;
        c.addChild(info);
      }
      world.addChild(c);
    });

    // Robots are topology resources. Until the backend models live pose, the art is a static
    // representative arm placed at the robot's logical cell position — no fake motion.
    (topology.robots || []).forEach((robot) => {
      const p = layout.robots[robot.id];
      if (!p) return;
      const s = isoToScreen(p.tx, p.ty);
      const constrained = topology.balance?.constraint === robot.id;
      const robotState = state?.robots?.[robot.id];
      const selected = selectedKey === robot.id;
      const profile = robotAssetProfile(robot.id);
      const footprint = profile.footprint;
      const floorContact = {
        x: (footprint.w - footprint.h) * TILE_W / 2,
        y: (footprint.w + footprint.h) * TILE_H / 2,
      };

      const c = new Container();
      c.x = s.x;
      c.y = s.y;
      c.zIndex = calculateDepthSort(p.tx, p.ty, 20);
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', () => onSelect(robot.id));

      const base = new Graphics();
      drawDiamond(base, footprint.w, footprint.h, 0x101d2c,
                  selected ? 0xe2e8f0 : constrained ? 0xa89060 : 0x5f7188,
                  0.62, selected ? 4 : constrained ? 3 : 1.5);
      c.addChild(base);

      const texture = textureRef.current[profile.spriteUrl];
      if (texture) {
        const sprite = new Sprite(texture);
        sprite.anchor.set(profile.groundAnchor.x, profile.groundAnchor.y);
        const scale = profile.renderWidthPx / Math.max(1, sprite.texture.width);
        sprite.scale.set(scale);
        sprite.x = floorContact.x + profile.groundOffsetPx.x;
        sprite.y = floorContact.y + profile.groundOffsetPx.y;
        c.addChild(sprite);
      }

      if (debugAnchors) {
        const guide = new Graphics();
        guide.moveTo(floorContact.x - 14, floorContact.y);
        guide.lineTo(floorContact.x + 14, floorContact.y);
        guide.moveTo(floorContact.x, floorContact.y - 14);
        guide.lineTo(floorContact.x, floorContact.y + 14);
        guide.stroke({ color: 0x22d3ee, width: 2, alpha: 1 });
        guide.circle(floorContact.x, floorContact.y, 4);
        guide.fill({ color: 0x22d3ee, alpha: 1 });
        c.addChild(guide);
      }

      const name = makeText(robot.id, 10, 0xe2e8f0, '800');
      name.x = floorContact.x;
      name.y = floorContact.y - profile.labelLiftPx;
      c.addChild(name);

      const rate = makeText(`${robot.piecesPerHour} pcs/h`, 8,
                            constrained ? 0xa89060 : 0x8ba0b5, '700');
      rate.x = floorContact.x;
      rate.y = floorContact.y - profile.statusLiftPx;
      c.addChild(rate);

      if (!robotState?.modelled) {
        const unknown = makeText('STATIC / POSE NOT MODELLED', 7, 0x6f8195, '700');
        unknown.x = floorContact.x;
        unknown.y = floorContact.y + 28;
        c.addChild(unknown);
      }

      if (constrained) {
        const flag = makeText('LINE CONSTRAINT', 8, 0xa89060, '900');
        flag.x = floorContact.x;
        flag.y = floorContact.y - profile.labelLiftPx - 18;
        c.addChild(flag);
      }
      world.addChild(c);
    });

    world.sortChildren();
    // Fit ONCE per topology, never on a live tick: the view must fill the space it is given, but a
    // refit every second would fight the user's own pan and zoom.
    if (!fittedRef.current) {
      const viewport = viewportRef.current;
      const bounds = world.getLocalBounds();
      if (viewport && bounds.width > 0 && bounds.height > 0) {
        const pad = 1.12;
        const zoom = Math.min(viewport.screenWidth / (bounds.width * pad),
                              viewport.screenHeight / (bounds.height * pad));
        viewport.setZoom(Math.max(0.2, Math.min(2.4, zoom)), true);
        viewport.moveCenter(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
        fittedRef.current = true;
      }
    }
  }, [debugAnchors, layout, onSelect, ready, selectedKey, showRobotLinks, showRoute, state, topology]);

  // A different line (or a re-fetched topology) is a different shape — fit it again.
  useEffect(() => { fittedRef.current = false; }, [topology?.id, topology?.stations?.length]);

  return (
    <div style={{ position: 'relative', height: 'min(68vh, 720px)', minHeight: 500, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(168,144,96,0.16)' }}>
      <div ref={hostRef} style={{ width: '100%', height: '100%', touchAction: 'none' }} />
      <div style={{
        position: 'absolute', left: 14, bottom: 12, pointerEvents: 'none',
        background: 'rgba(13,27,42,0.82)', border: '1px solid rgba(139,160,181,0.16)',
        borderRadius: 8, padding: '7px 10px', color: 'var(--cn-muted)', fontSize: 10,
      }}>
{debugAnchors ? 'cyan crosshair = sprite floor contact · tune art in lineAssets.js, never tx/ty' : 'drag to pan · wheel/pinch to zoom · robots remain static until the engine models their position'}
      </div>
    </div>
  );
}
